import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Copy, RefreshCw, AlertTriangle, TrendingDown, Users, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../hooks/use-toast';
import api from '../services/api';

const fmtINR = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN')}`;
const pct = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0);

const StatRow = ({ label, value, danger }) => (
  <div className="flex justify-between py-2 border-b border-gray-100 last:border-0">
    <span className="text-sm text-gray-600">{label}</span>
    <span className={`text-sm font-semibold ${danger ? 'text-red-600' : 'text-gray-900'}`}>{value}</span>
  </div>
);

const LeadHealthAudit = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [showTextReport, setShowTextReport] = useState(false);
  const [textReport, setTextReport] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/lead-health-audit');
      setData(res.data);
    } catch (e) {
      toast({
        title: 'Failed to load audit',
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const buildTextReport = (d) => {
    const lines = [];
    lines.push('='.repeat(60));
    lines.push('LEAD SESSIONS — HEALTH AUDIT REPORT');
    lines.push(`Generated: ${d.generated_at}`);
    lines.push('='.repeat(60));

    lines.push('\n[1] TOTALS');
    lines.push(`  Sessions: ${d.totals.sessions}`);
    lines.push(`  Leads:    ${d.totals.leads}`);

    lines.push('\n[2] SESSION STATUS');
    d.session_status.forEach(s => lines.push(`  ${s.status}: ${s.count}`));

    lines.push('\n[3] LEAD STATUS');
    d.lead_status.forEach(s => lines.push(`  ${s.status}: ${s.count} leads   |   value = ${fmtINR(s.value)}`));

    lines.push('\n[4] AGE OF ACTIVE (open+parked) LEADS');
    d.age_buckets.forEach(b => lines.push(`  ${b.bucket}: ${b.count}`));

    lines.push(`\n[5] OPEN leads with NO follow_up_date: ${d.open_no_followup.count} / ${d.open_no_followup.total}`);

    lines.push(`\n[6] PARKED leads with OVERDUE follow_up_date: ${d.parked_overdue.count} / ${d.parked_overdue.total}`);
    lines.push('    Overdue depth:');
    d.parked_overdue.depth.forEach(b => lines.push(`      ${b.bucket}: ${b.count}`));
    lines.push(`    Parked & not touched > 14d: ${d.parked_overdue.stale_not_touched_gt14d}`);

    lines.push('\n[7a] PARKED REASONS');
    d.parked_reasons.forEach(r => lines.push(`  ${r.reason}: ${r.count}`));

    lines.push('\n[7b] LOST REASONS');
    d.lost_reasons.forEach(r => lines.push(`  ${r.reason}: ${r.count}`));

    lines.push('\n[7c] WON REASONS');
    d.won_reasons.forEach(r => lines.push(`  ${r.reason}: ${r.count}`));

    lines.push('\n[8] ROT PER SALESMAN (active leads > 30 / 60 / 90 days old)');
    d.rot_per_salesman.forEach(s => {
      lines.push(`  ${s.salesman_name.padEnd(25)} active=${String(s.active).padStart(3)}  >30d=${String(s.gt30).padStart(3)}  >60d=${String(s.gt60).padStart(3)}  >90d=${String(s.gt90).padStart(3)}  rotting=${fmtINR(s.rotting_value)}`);
    });

    lines.push(`\n[9] OPEN SESSIONS WITH ZERO LEADS: ${d.zero_lead_sessions.count} / ${d.zero_lead_sessions.total_open_sessions}`);

    lines.push('\n[10] CONVERSION MATH');
    lines.push(`  Decided conversion (dashboard %): ${d.conversion.decided_conversion_pct}%`);
    lines.push(`  TRUE conversion (won / all):      ${d.conversion.true_conversion_pct}%`);
    lines.push(`  Undecided (open+parked):          ${d.conversion.undecided_count} (${d.conversion.undecided_pct}% of pipeline)`);
    lines.push(`  Value stuck (open+parked):        ${fmtINR(d.conversion.value_undecided)}`);
    lines.push(`  Value won:                        ${fmtINR(d.conversion.value_won)}`);
    lines.push(`  Value lost:                       ${fmtINR(d.conversion.value_lost)}`);

    lines.push('\n' + '='.repeat(60));
    lines.push('END OF REPORT — paste into E1 chat for analysis');
    lines.push('='.repeat(60));
    return lines.join('\n');
  };

  const handleGenerateText = () => {
    if (!data) return;
    const txt = buildTextReport(data);
    setTextReport(txt);
    setShowTextReport(true);
    setTimeout(() => {
      document.getElementById('audit-textarea')?.focus();
    }, 200);
  };

  const handleCopyText = async () => {
    try {
      await navigator.clipboard.writeText(textReport);
      toast({ title: 'Copied to clipboard' });
    } catch {
      // Fallback: select the textarea so user can long-press → copy
      const ta = document.getElementById('audit-textarea');
      if (ta) { ta.select(); ta.setSelectionRange(0, textReport.length); }
      toast({ title: 'Long-press the text and tap Copy' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-gray-500 mt-4">Loading audit…</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <p className="text-red-600">Unable to load audit.</p>
        <Button onClick={load} className="mt-4" data-testid="retry-audit">Retry</Button>
      </div>
    );
  }

  const d = data;
  const totalActive = d.age_buckets.reduce((a, b) => a + b.count, 0);
  const oldLeads = d.age_buckets.filter(b => ['31-60d', '61-90d', '90d+'].includes(b.bucket)).reduce((a, b) => a + b.count, 0);
  const rotPctActive = pct(oldLeads, totalActive);

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      {/* Header */}
      <div className="bg-[#075E54] text-white px-4 py-3 shadow-md sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-white hover:bg-emerald-700 p-2" data-testid="audit-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold truncate">Lead Health Audit</h1>
            <p className="text-xs text-emerald-100">Live production diagnostics</p>
          </div>
          <Button size="sm" variant="ghost" onClick={load} className="text-white hover:bg-emerald-700" data-testid="audit-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4 space-y-4">
        {/* Copy-Report Card (moved to top so it's the first thing seen on mobile) */}
        <Card className="border-emerald-300 bg-emerald-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Download className="w-4 h-4" /> Copy Report as Text
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-gray-600 mb-3">Generate a plain-text version of this report you can paste back into the chat.</p>
            {!showTextReport ? (
              <Button onClick={handleGenerateText} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="generate-text-report">
                Generate Copyable Report
              </Button>
            ) : (
              <div className="space-y-2">
                <Textarea
                  id="audit-textarea"
                  value={textReport}
                  readOnly
                  className="font-mono text-xs h-64"
                  onFocus={(e) => e.target.select()}
                  data-testid="audit-textarea"
                />
                <div className="flex gap-2">
                  <Button onClick={handleCopyText} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="copy-audit-text">
                    <Copy className="w-4 h-4 mr-2" /> Copy All
                  </Button>
                  <Button variant="outline" onClick={() => setShowTextReport(false)} data-testid="hide-audit-text">Hide</Button>
                </div>
                <p className="text-[11px] text-gray-500">Tip: On mobile, tap inside the box then long-press → Select All → Copy.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Headline KPIs */}
        <div className="grid grid-cols-2 gap-3">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-4">
              <p className="text-xs text-gray-500">Total Leads</p>
              <p className="text-2xl font-bold">{d.totals.leads}</p>
              <p className="text-xs text-gray-500">{d.totals.sessions} sessions</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="pt-4">
              <p className="text-xs text-gray-500">TRUE Conversion</p>
              <p className="text-2xl font-bold text-red-600">{d.conversion.true_conversion_pct}%</p>
              <p className="text-xs text-gray-500">Dashboard shows {d.conversion.decided_conversion_pct}%</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="pt-4">
              <p className="text-xs text-gray-500">Undecided Pipeline</p>
              <p className="text-2xl font-bold text-amber-600">{d.conversion.undecided_count}</p>
              <p className="text-xs text-gray-500">{d.conversion.undecided_pct}% of all leads</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="pt-4">
              <p className="text-xs text-gray-500">Rot (&gt;30d active)</p>
              <p className="text-2xl font-bold text-orange-600">{oldLeads}</p>
              <p className="text-xs text-gray-500">{rotPctActive}% of active</p>
            </CardContent>
          </Card>
        </div>

        {/* Value Stuck vs Won */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-red-500" /> Value Comparison
            </CardTitle>
          </CardHeader>
          <CardContent>
            <StatRow label="Value WON" value={fmtINR(d.conversion.value_won)} />
            <StatRow label="Value LOST" value={fmtINR(d.conversion.value_lost)} />
            <StatRow label="Value PARKED (stuck)" value={fmtINR(d.conversion.value_parked)} danger={d.conversion.value_parked > d.conversion.value_won} />
            <StatRow label="Value OPEN (undecided)" value={fmtINR(d.conversion.value_open)} />
            <StatRow label="Total UNDECIDED value" value={fmtINR(d.conversion.value_undecided)} danger />
            {d.conversion.value_parked > d.conversion.value_won && (
              <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                <AlertTriangle className="w-3 h-3 inline mr-1" />
                Parked value ({fmtINR(d.conversion.value_parked)}) exceeds Won value — deferral is disguised loss.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Critical Gaps */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500" /> Critical Gaps
            </CardTitle>
          </CardHeader>
          <CardContent>
            <StatRow
              label="OPEN leads with NO follow-up date"
              value={`${d.open_no_followup.count} / ${d.open_no_followup.total} (${pct(d.open_no_followup.count, d.open_no_followup.total)}%)`}
              danger={d.open_no_followup.count > 0}
            />
            <StatRow
              label="PARKED leads with OVERDUE follow-up"
              value={`${d.parked_overdue.count} / ${d.parked_overdue.total} (${pct(d.parked_overdue.count, d.parked_overdue.total)}%)`}
              danger={d.parked_overdue.count > 0}
            />
            <StatRow
              label="PARKED & not touched > 14 days"
              value={`${d.parked_overdue.stale_not_touched_gt14d} / ${d.parked_overdue.total}`}
              danger={d.parked_overdue.stale_not_touched_gt14d > 0}
            />
            <StatRow
              label="Walk-in sessions with 0 leads recorded"
              value={`${d.zero_lead_sessions.count} / ${d.zero_lead_sessions.total_open_sessions}`}
              danger={d.zero_lead_sessions.count > 0}
            />
          </CardContent>
        </Card>

        {/* Age Buckets */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4" /> Age of Active Leads
            </CardTitle>
          </CardHeader>
          <CardContent>
            {d.age_buckets.map(b => {
              const isBad = ['31-60d', '61-90d', '90d+'].includes(b.bucket);
              return <StatRow key={b.bucket} label={b.bucket} value={b.count} danger={isBad && b.count > 0} />;
            })}
          </CardContent>
        </Card>

        {/* Overdue depth */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Parked Follow-up — How Overdue?</CardTitle>
          </CardHeader>
          <CardContent>
            {d.parked_overdue.depth.map(b => {
              const isBad = ['31-90d', '90d+'].includes(b.bucket);
              return <StatRow key={b.bucket} label={`Overdue ${b.bucket}`} value={b.count} danger={isBad && b.count > 0} />;
            })}
          </CardContent>
        </Card>

        {/* Rot per salesman */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4" /> Rot Per Salesman
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b text-gray-500 text-left">
                  <th className="py-2 pr-2">Salesman</th>
                  <th className="py-2 px-1 text-right">Active</th>
                  <th className="py-2 px-1 text-right">&gt;30d</th>
                  <th className="py-2 px-1 text-right">&gt;90d</th>
                  <th className="py-2 pl-1 text-right">Rotting ₹</th>
                </tr>
              </thead>
              <tbody>
                {d.rot_per_salesman.map(s => (
                  <tr key={s.salesman_name} className="border-b border-gray-100">
                    <td className="py-2 pr-2 font-medium">{s.salesman_name}</td>
                    <td className="py-2 px-1 text-right">{s.active}</td>
                    <td className={`py-2 px-1 text-right ${s.gt30 > 0 ? 'text-orange-600 font-semibold' : ''}`}>{s.gt30}</td>
                    <td className={`py-2 px-1 text-right ${s.gt90 > 0 ? 'text-red-600 font-semibold' : ''}`}>{s.gt90}</td>
                    <td className={`py-2 pl-1 text-right ${s.rotting_value > 100000 ? 'text-red-600 font-semibold' : ''}`}>
                      {fmtINR(s.rotting_value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Reasons */}
        <div className="grid grid-cols-1 gap-3">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Parked Reasons</CardTitle></CardHeader>
            <CardContent>{d.parked_reasons.map((r, i) => <StatRow key={i} label={r.reason} value={r.count} />)}</CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Lost Reasons</CardTitle></CardHeader>
            <CardContent>
              {d.lost_reasons.map((r, i) => (
                <StatRow key={i} label={r.reason} value={r.count} danger={r.reason === 'Other'} />
              ))}
              {d.lost_reasons.find(r => r.reason === 'Other') && (
                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                  &quot;Other&quot; reasons are un-analysable — consider retiring or forcing a comment.
                </div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Won Reasons</CardTitle></CardHeader>
            <CardContent>{d.won_reasons.map((r, i) => <StatRow key={i} label={r.reason} value={r.count} />)}</CardContent>
          </Card>
        </div>

        <p className="text-center text-[11px] text-gray-400 pt-4">
          Report generated at {new Date(d.generated_at).toLocaleString()}
        </p>
      </div>
    </div>
  );
};

export default LeadHealthAudit;
